// Copyright TraderEvolution Global LTD. © 2017-2024. All rights reserved.
import { InstrumentUtils } from '../../../Utils/Instruments/InstrumentUtils.js';
import { DataStatusEnum, TvSessionIdEnum } from '../TradingViewPrimitives/TvEnums';
import { type SearchSymbolResultItem, type SymbolResolveExtension, type Timezone } from '../../../Libs/TradingViewChart/charting_library_cloned_data/charting_library/charting_library';
import { TvSearchSymbolResultItem } from '../TradingViewPrimitives/TvSearchSymbolResultItem';
import { TvSymbol } from '../TradingViewPrimitives/TvSymbol';
import { type Instrument, type VariableTick } from '../../cache/Instrument.js';
import { TvSessionConvertor } from './TvSessionConvertor';
import { Periods } from '../../../Utils/History/TFInfo';
import { TvSymbolInfoPriceSource } from '../TradingViewPrimitives/TvSymbolInfoPriceSource';
import { Resources } from '../../properties/Resources.js';
import { type TvSubsessionInfo } from '../TradingViewPrimitives/TvSubsessionInfo.js';
import { TvTimeZoneHelper } from '../Helpers/TvTimeZoneHelper';
const fakeTvSymbolName: string = 'fakeTVSymbol';
export class TvSymbolConvertor {
    #sessionConvertor = new TvSessionConvertor();

    convertToTvSymbol (instrument: Instrument, extension: SymbolResolveExtension): TvSymbol {
        const sessionPeriods = instrument.TradingSessionsList;
        const firstSessionPeriod = instrument.GetZeroTradingSession();
        const holidays = this.#sessionConvertor.getHolidays(firstSessionPeriod);

        const tvSubsessions: TvSubsessionInfo[] = this.#sessionConvertor.getSubsessions(sessionPeriods);
        const subsessionID = extension?.session ?? TvSessionIdEnum.regular;
        const tvSubSession = tvSubsessions.find(x => x.id === subsessionID);
        const tvSession = tvSubSession?.session ?? '';
        const corrections = tvSubSession?.getCorrection();

        const precision = instrument.getPrecision();
        const priceScale = Math.pow(10, precision);
        const mintickSize = instrument.GetPointSize(null);
        const minmov = mintickSize * priceScale;

        const tvSymbol = new TvSymbol();
        tvSymbol.ticker = instrument.GetInteriorID();
        tvSymbol.name = instrument.DisplayName();
        tvSymbol.full_name = this.getFullname(instrument);
        tvSymbol.description = instrument.DescriptionValue();
        tvSymbol.type = instrument.getTypeString();

        tvSymbol.pricescale = priceScale;
        tvSymbol.minmov = minmov;

        tvSymbol.volume_precision = instrument.getAmountPrecision();
        tvSymbol.exchange = instrument.TradingExchange;
        tvSymbol.listed_exchange = instrument.MarketDataExchange;
        tvSymbol.sector = instrument.Sector;
        tvSymbol.industry = instrument.Industry;
        tvSymbol.session = tvSession;
        tvSymbol.session_holidays = holidays;
        tvSymbol.corrections = corrections;
        tvSymbol.subsession_id = subsessionID;
        tvSymbol.subsessions = tvSubsessions;
        tvSymbol.timezone = TvTimeZoneHelper.getTimeZoneForTV(firstSessionPeriod) as Timezone;

        const logoUrls = this.getLogoUrls(instrument);
        if (logoUrls) {
            tvSymbol.logo_urls = logoUrls;
        }

        if (instrument.isOptionSymbol || (instrument.isFuturesSymbol && !instrument.IsContiniousContract)) {
            if (instrument.ExpDateReal.getFullYear() > 2000 && instrument.ExpDateReal < new Date()) {
                tvSymbol.expired = true;
                tvSymbol.expiration_date = instrument.ExpDateReal.getTime() / 1000;
            }
        }

        if (instrument.VariableTickList.length > 1) {
            let variableString: string = '';
            // the first interval (from negative infinity to 0) was skipped
            for (let i = 1; i < instrument.VariableTickList.length; i++) {
                const tick: VariableTick = instrument.VariableTickList[i];

                if (i == instrument.VariableTickList.length - 1) {
                    variableString += `${tick.PointSize}`;
                } else {
                    variableString += `${tick.PointSize} ${tick.RightBorder} `;
                }
            }
            tvSymbol.variable_tick_size = variableString;
        }

        // неважно какой период передавать, главное чтобы не тики
        const allowedHistoryTypes = instrument.DataCache.GetAllowedHistoryTypesByPeriod(Periods.MIN);

        const priceSources: TvSymbolInfoPriceSource[] = [];
        for (const historyType of allowedHistoryTypes) {
            const historyTypeId = historyType;
            const historyTypeLocalization = Resources.getResource('chart.view.source' + historyType);
            const priceSource: TvSymbolInfoPriceSource = new TvSymbolInfoPriceSource();
            priceSource.id = historyTypeId;
            priceSource.name = historyTypeLocalization;
            priceSources.push(priceSource);
        }

        if (priceSources.length > 0) {
            tvSymbol.price_sources = priceSources;
            tvSymbol.price_source_id = instrument.DefaultChartHistoryType.toString();
        }

        if (instrument.QuoteDelay > 0) {
            tvSymbol.data_status = DataStatusEnum.delayed_streaming;
            tvSymbol.delay = instrument.QuoteDelay * 60; // in seconds
        } else if (instrument.QuoteCanGetSnapshot) {
            tvSymbol.data_status = DataStatusEnum.endofday;
            tvSymbol.delay = -1; // for endofday
        }

        return tvSymbol;
    }

    getFullname (instrument) {
        const symbolWithoutRouteName = InstrumentUtils.RemoveRouteName(instrument.DisplayName());
        const exchange = instrument.TradingExchange;
        return `${symbolWithoutRouteName}:${exchange}`;
    }

    private getLogoUrls (instrument: Instrument): [string] | [string, string] | null {
        if (instrument.LogoAddress) {
            return [instrument.LogoAddress];
        }
        return null;
    }

    convertToTvSearchSymbolResultItem (instrument): SearchSymbolResultItem {
        const resultItem = new TvSearchSymbolResultItem();
        resultItem.description = instrument.DescriptionValue();
        resultItem.exchange = instrument.MarketDataExchange; // ?
        resultItem.full_name = this.getFullname(instrument);
        resultItem.symbol = instrument.DisplayName();
        resultItem.ticker = instrument.GetInteriorID();
        resultItem.type = instrument.getTypeString();
        const logoUrls = this.getLogoUrls(instrument);
        if (logoUrls) {
            resultItem.logo_urls = logoUrls;
        }
        return resultItem;
    }

    public isFakeSymbolName (symbolName: string | undefined): boolean {
        return !symbolName || symbolName === fakeTvSymbolName;
    }

    public getFakeSymbol (): TvSymbol {
        const fakeTvSymbol = new TvSymbol();
        fakeTvSymbol.name = fakeTvSymbol.full_name = fakeTvSymbol.ticker = fakeTvSymbolName;
        fakeTvSymbol.session = '24x7';
        return fakeTvSymbol;
    }
}
